/** La langue de la page, et la façon d'écrire une phrase dans les deux.
 *
 *  Le site est né en français et le restera par défaut : c'est la langue dans laquelle ses
 *  mesures ont été écrites. L'anglais s'ajoute pour que quelqu'un qui n'est pas francophone
 *  puisse s'en servir (Armand, 21/09/2026).
 *
 *  Pourquoi des PAIRES et pas un dictionnaire de clés : dans ce dépôt, chaque phrase vit à
 *  l'endroit où elle s'affiche, sous le commentaire qui explique pourquoi elle dit ça. Un
 *  dictionnaire séparerait les deux, et la prochaine personne corrigerait la phrase sans
 *  voir la raison. Avec `t(fr, en)`, les deux versions restent côte à côte, et TypeScript
 *  refuse qu'il en manque une.
 */
export type Lang = 'fr' | 'en'

const KEY = 'ai-cost.lang'
const SITE = 'https://real-ai-cost.saru-lab.com'

let lang: Lang = 'fr'

/** La langue du navigateur, si la page ne sait pas encore. Un visiteur qui n'a jamais
 *  choisi lit dans sa langue, pas dans la nôtre. */
function preferred(): Lang {
  const declared = typeof navigator === 'undefined' ? '' : (navigator.language ?? '')
  return declared.toLowerCase().startsWith('fr') ? 'fr' : 'en'
}

/** La langue portée par l'adresse : /en. Une seule page ne s'indexe que dans une langue,
 *  et un moteur ne clique pas sur une pastille — il suit des URL (21/09/2026). */
function fromPath(): Lang | null {
  if (typeof location === 'undefined') return null
  return /^\/en(\/|$)/.test(location.pathname) ? 'en' : null
}

export function initLang(): Lang {
  let stored: string | null = null
  try { stored = localStorage.getItem(KEY) } catch { stored = null }
  // L'adresse tranche avant tout : un lien partagé vers /en s'ouvre en anglais, même
  // pour quelqu'un qui avait choisi le français ici.
  lang = fromPath() ?? (stored === 'fr' || stored === 'en' ? stored : preferred())
  applyLang()
  return lang
}

export function getLang(): Lang {
  return lang
}

export function setLang(next: Lang): void {
  lang = next
  try { localStorage.setItem(KEY, next) } catch { /* navigation privée : tant pis */ }
  // L'adresse suit le choix, sans rechargement : elle reste partageable et indexable.
  if (typeof history !== 'undefined' && typeof location !== 'undefined') {
    const path = next === 'en' ? '/en' : '/'
    if (location.pathname !== path) history.replaceState(null, '', path + location.search)
  }
  applyLang()
}

/** Ce qui vit hors de la vue : l'attribut de langue, le titre de l'onglet et la
 *  description que partagent les moteurs et les messageries. index.html les porte en
 *  français — c'est la langue par défaut du site ; ici on les suit. */
function applyLang(): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = lang
  document.title = t('Real AI Cost — combien coûte un projet confié à une IA',
    'Real AI Cost — what a project handed to an AI actually costs')
  const canonical = document.querySelector('link[rel="canonical"]')
  canonical?.setAttribute('href', `${SITE}${lang === 'en' ? '/en' : '/'}`)
  const description = document.querySelector('meta[name="description"]')
  description?.setAttribute('content', t(
    'Choisissez ce que vous voulez faire faire. Vous obtenez ce que ça coûterait confié à '
    + 'une IA, et la comparaison avec une réalisation humaine.',
    'Pick what you want done. You get what it would cost handed to an AI, and how that '
    + 'compares with having people build it.'))
}

/** Une phrase, dans les deux langues. */
export function t(fr: string, en: string): string {
  return lang === 'fr' ? fr : en
}

/** Le même choix, pour autre chose qu'une chaîne : un tableau de libellés, un nœud. */
export function pick<T>(fr: T, en: T): T {
  return lang === 'fr' ? fr : en
}
